import { TenantCurrency, formatMoney } from 'src/common/currency';
import { OwnerShare, formatOwnerShare } from 'src/financial/financial-data/ownerShares';
import { GroupMember } from '../grouping';
import { CashflowColumn } from '../components/CashflowTable';
import { amountCell, frequencyAmountCell } from '../components/cashflowCells';
import { labelForIncomeType } from '../incomeTypes';
import { NOT_RECORDED, dateOrNotRecorded } from '../notRecorded';
import { ExpenditureRow, IncomeRow, LifeEventRow, OwnerPart } from '../rows';
import { REASON_LABELS } from '../leftOut';
import { CashflowFrequency, Period, amountForPeriod, leftOutReason } from '../utils';
import { AppliesToCell } from './AppliesToCell';
import { labelForExpenditureCategory } from './expenditureCategories';

type OwnedRow = { members: GroupMember[]; ownerShare?: OwnerShare; ownerParts?: OwnerPart[] };

/**
 * Who holds an item, as the Financial tab shows it: on one client's tab, that client's share; on
 * the family tab, where no single member is "me", the owners by name, with each one's part of a
 * shared item on hover so the adviser can see whose money it is without opening it.
 */
function ownershipColumn<T extends OwnedRow>(
  familyView: boolean,
  describePart: (row: T, part: OwnerPart) => string,
): CashflowColumn<T> {
  return {
    key: 'members',
    header: familyView ? 'Family owners' : 'Ownership',
    renderValue: (_value, row) =>
      !familyView && row.ownerShare ? (
        formatOwnerShare(row.ownerShare)
      ) : (
        <AppliesToCell
          members={row.members}
          parts={row.ownerParts?.map(
            part => `${part.name}: ${formatOwnerShare(part.share)}, ${describePart(row, part)}`,
          )}
        />
      ),
  };
}

/** An amount converted to the period the tab shows, or as recorded where it cannot convert. */
function partForPeriod(
  amount: number | null | undefined,
  frequency: CashflowFrequency | null,
  period: Period,
  currency: TenantCurrency,
): string {
  if (amount == null) return 'not recorded';
  return formatMoney(amountForPeriod(amount, frequency, period) ?? amount, currency);
}

/**
 * What the converted parts are a figure for. A one-off, variable or missing frequency cannot be
 * converted, so its parts are as recorded and say so rather than claiming the tab's period.
 */
function partPeriodLabel(frequency: CashflowFrequency | null, period: Period): string {
  const reason = leftOutReason(frequency);
  if (reason === null) return period;
  const label = REASON_LABELS[reason].toLowerCase();
  return reason === 'noFrequency' ? label : `${label}, as recorded`;
}

export function incomeRowColumns(
  period: Period,
  currency: TenantCurrency,
  familyView: boolean,
): CashflowColumn<IncomeRow>[] {
  return [
    ownershipColumn<IncomeRow>(
      familyView,
      (row, part) =>
        `${partForPeriod(part.amount, row.frequency, period, currency)} before tax, ` +
        `${partForPeriod(part.amountPostTax, row.frequency, period, currency)} after tax ` +
        `(${partPeriodLabel(row.frequency, period)})`,
    ),
    {
      key: 'incomeType',
      header: 'Type',
      renderValue: value => labelForIncomeType(String(value)),
    },
    { key: 'description', header: 'Description' },
    { key: 'frequency', header: 'Frequency' },
    {
      key: 'amountPreTax',
      header: `Before tax (${period})`,
      renderValue: (_value, row) =>
        frequencyAmountCell(period, row.amountPreTax, row.frequency, currency),
    },
    {
      key: 'amountPostTax',
      header: `After tax (${period})`,
      renderValue: (_value, row) =>
        frequencyAmountCell(period, row.amountPostTax, row.frequency, currency, {
          partialNote: row.postTaxNote,
        }),
    },
  ];
}

export function expenditureRowColumns(
  period: Period,
  currency: TenantCurrency,
  familyView: boolean,
): CashflowColumn<ExpenditureRow>[] {
  return [
    ownershipColumn<ExpenditureRow>(
      familyView,
      (row, part) =>
        `${partForPeriod(part.amount, row.frequency, period, currency)} ` +
        `(${partPeriodLabel(row.frequency, period)})`,
    ),
    {
      key: 'category',
      header: 'Category',
      renderValue: (_value, row) =>
        row.category ? labelForExpenditureCategory(row.category) : NOT_RECORDED,
    },
    { key: 'description', header: 'Description' },
    { key: 'frequency', header: 'Frequency' },
    {
      key: 'amount',
      header: `Spend (${period})`,
      renderValue: (_value, row) =>
        frequencyAmountCell(period, row.amount, row.frequency, currency),
    },
  ];
}

export function lifeEventRowColumns(
  currency: TenantCurrency,
  familyView: boolean,
): CashflowColumn<LifeEventRow>[] {
  return [
    ownershipColumn<LifeEventRow>(familyView, (_row, part) =>
      part.amount == null ? 'not recorded' : formatMoney(part.amount, currency),
    ),
    { key: 'description', header: 'Description' },
    {
      key: 'eventDate',
      header: 'Date',
      renderValue: (_value, row) => dateOrNotRecorded(row.eventDate),
    },
    { key: 'details', header: 'Details' },
    {
      key: 'cost',
      header: 'Cost',
      renderValue: (_value, row) => amountCell(row.cost, currency),
    },
  ];
}
